// `confirm` — what is about to be saved, the `validate_tree` dry run's
// findings (listed by the shell, each with "Go to step"), and an explicit
// confirmation (docs/plans/transaction-wizard.md §6.6).
//
// `confirm.password` (regulated captures): the person re-enters their OWN
// password, checked on the server by the host's confirmation provider — the
// one an action button's password confirmation uses (`wizard_confirm`). Next
// stays blocked until it is verified; the password is sent once and never
// kept.

import { useState } from 'react'
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react'
import { columnOf, headingOf, keyOf, parseTarget, readCol, text } from '../model'
import { BAD, GOOD, T, banner, button, card, input as inputStyle, muted, table, td, th } from '../styles'
import { tw } from '../../testHooks'
import { Check, display, type StepProps } from './common'

function PasswordConfirm({ ctl, handheld, label }: StepProps & { label: string }) {
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const verified = ctl.ui.confirmed
  const submit = async () => {
    if (!password || checking) return
    setChecking(true)
    setError(null)
    try {
      const res = await ctl.confirmPassword(password)
      if (!res.verified) setError(res.message || 'Incorrect password. Please try again.')
    } finally {
      // Never kept, right or wrong.
      setPassword('')
      setChecking(false)
    }
  }
  if (verified) {
    return (
      <div style={banner(GOOD)} {...tw('field', '_password', { verified: true })}>
        <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{label} — confirmed with your password.</span>
      </div>
    )
  }
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 600, fontSize: handheld ? 16 : 14 }}>{label}</div>
      <div style={muted}>Enter your password to confirm.</div>
      <form onSubmit={e => { e.preventDefault(); void submit() }}
        style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          // Enter checks the password; it must not also press Next.
          onKeyDown={e => { if (e.key === 'Enter') e.stopPropagation() }}
          autoComplete="current-password" aria-label="Your password" placeholder="Your password"
          style={{ ...inputStyle(handheld), flex: 1 }} {...tw('field', '_password', { error: !!error })} />
        <button type="submit" disabled={!password || checking} style={button(handheld, true, !password || checking)}
          {...tw('action', 'confirm_password')}>
          {checking ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <KeyRound size={16} />}
          Confirm
        </button>
      </form>
      {error && <div style={{ ...banner(BAD), fontSize: 14 }} role="alert">{error}</div>}
    </div>
  )
}

export default function ConfirmStep({ ctl, handheld }: StepProps) {
  const { step, scope, fi, frame, model, ui } = ctl
  if (!step || !scope || !fi || !frame || !model) return null
  const form = String(step.form_no || model.line_form || fi.lineForm)
  const header: Array<{ label: string; value: unknown }> = []
  const tables: Array<{ form: string; cols: string[] }> = []
  for (const item of step.show || []) {
    if (item.startsWith('lines')) {
      const m = /^lines(\d*):(.*)$/.exec(item)
      if (m) tables.push({ form: m[1] || form, cols: m[2].split(',').map(c => c.trim()).filter(Boolean) })
      continue
    }
    const t = parseTarget(item, fi, form)
    if (!t) continue
    const v = t.scope === 'var' ? frame.vars[t.name]
      : t.scope === 'header' ? readCol(frame.record.header, fi, t.formNo, t.name)
        : readCol(scope.line, fi, t.formNo, t.name)
    header.push({ label: t.scope === 'var' ? t.name : headingOf(fi, t.formNo, t.name), value: v })
  }
  const hasErrors = ctl.issues.some(i => i.type === 'E')
  const label = step.confirm?.label ? text(step.confirm.label, scope) : 'I confirm this is correct'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {header.length > 0 && (
        <div style={{ ...card, display: 'grid', gridTemplateColumns: handheld ? '1fr' : 'minmax(140px, max-content) 1fr', gap: handheld ? 4 : '6px 16px' }}>
          {header.map((h, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <div style={{ ...muted, fontWeight: 600 }}>{h.label}</div>
              <div style={{ fontSize: handheld ? 16 : 14, marginBottom: handheld ? 8 : 0 }}>{display(h.value) || '—'}</div>
            </div>
          ))}
        </div>
      )}
      {tables.map((tb, ti) => {
        const rows = (frame.record.details[tb.form] || []).filter(r => !r._deleted)
        return (
          <div key={ti} style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <table style={table}>
              <thead><tr>{tb.cols.map(c => <th key={c} style={th}>{headingOf(fi, tb.form, c)}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {tb.cols.map(c => {
                      const col = columnOf(fi, tb.form, c)
                      return <td key={c} style={td}>{display(col ? r[keyOf(fi, tb.form, c)] : readCol(r, fi, tb.form, c))}</td>
                    })}
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={tb.cols.length} style={{ ...td, ...muted }}>No lines.</td></tr>}
              </tbody>
            </table>
          </div>
        )
      })}
      {step.validate && !hasErrors && !ctl.busy && (
        <div style={{ ...muted }}>Checked — nothing stops this from being saved.</div>
      )}
      {step.confirm?.password ? (
        <PasswordConfirm ctl={ctl} handheld={handheld} label={label} />
      ) : step.confirm && (
        <Check checked={ui.confirmed} onChange={v => ctl.setUi({ confirmed: v })} handheld={handheld}
          label={label} hook="_confirm" />
      )}
    </div>
  )
}
